/* eslint-disable react/jsx-props-no-spreading */
import { rgba } from 'polished'
import React from 'react'
import styled from 'styled-components'

import config from '../../../../../config'
import { mediaMax } from '../../../../constants/breakpoints'
import { LISAImages } from '../../../../types/lisa'
import { Expo, UnionWindows } from '../../../../types/viewModels'
import { ImageInWindow, RoomSide, RoomSideEnum } from '../../../RoomExperience'
import StoryItem, { StoryItemProps } from '../../../RoomExperience/components/StoryItem'
// TODO: Should not be default
import Wallpaper, {
  WallpaperBack, WallpaperFloor, WallpaperShadowLeft,
  WallpaperShadowRight,
  WallpaperShadowTopBottom,
  WallpaperTop,
} from '../../../RoomExperience/components/Wallpaper'

// This is just a filler component so we can keep the conditional statements and logic
// compact and hold dear to the kiss principle.
interface WindowProps extends StoryItemProps {
  imageAsset?: LISAImages
  mobileWidth?: number
}

const Window: React.FC<WindowProps> = ({
  type, href, imageAsset, onClick, mobileWidth, ...rest
}) => {
  if (type === 'Outbound' && href) {
    return (
      <StyledStoryItem mobileWidth={mobileWidth} {...rest} href={href} isOutboundLink type={type}>
        {imageAsset
          && (
            <ImageInWindow
              src={`${config.LISA_IMAGE_BASE}${imageAsset[0].assetId}&action=width:600`}
              alt="custom image for window"
              aria-hidden="true"
            />
          )}
      </StyledStoryItem>
    )
  } if (type === 'Video' && href && onClick) {
    return (
      <StyledStoryItem mobileWidth={mobileWidth} {...rest} href={href} type={type} onClick={() => onClick()}>
        {imageAsset && (
          <ImageInWindow
            src={`${config.LISA_IMAGE_BASE}${imageAsset[0].assetId}&action=width:600`}
            alt="custom image for window"
            aria-hidden="true"
          />
        )}
      </StyledStoryItem>
    )
  }
  if (type === 'Inbound' && href) {
    return (
      <StyledStoryItem mobileWidth={mobileWidth} {...rest} href={href} type={type}>
        {imageAsset && (
          <ImageInWindow
            src={`${config.LISA_IMAGE_BASE}${imageAsset[0].assetId}&action=width:600`}
            alt="custom image for window"
            aria-hidden="true"
          />
        )}
      </StyledStoryItem>
    )
  }
  return null
}

interface RoomProps {
  room: Expo
  windowOnMouseEnter: (title?: string, description?: string) => void
  windowOnMouseLeave: () => void
  className?: string
  showVideo: (src: string) => void
  color?: string
  mobileView: boolean
}

export const Room: React.FC<RoomProps> = ({
  room, windowOnMouseEnter, windowOnMouseLeave, className, showVideo, color, mobileView,
}) => {
  const sortedWindows = room.windows.sort((x, y) => x.order - y.order)

  const numWindows = sortedWindows.length
  const margin = 5
  const mobileWindowWidth = numWindows > 0 ? (100 - (numWindows * margin)) / numWindows : 0
  const windowLabelWidth = 100

  const backOrMobileWindow = (window: UnionWindows) => {
    return mobileView
      ? (
        <WindowAndLabel mobileWidth={mobileWindowWidth}>
          <Window
            key={`${window.title}-${window.order}`}
            width={`${mobileWindowWidth}%`}
            type={window.type}
            onClick={() => (window.type === 'Video' ? showVideo(window.iframesrc) : '')}
            href={window.type === 'Video' ? window.iframesrc : window.url}
            imageAsset={window.image}
            onMouseEnter={() => windowOnMouseEnter(window.title, window.description)}
            onMouseLeave={windowOnMouseLeave}
            mobileWidth={mobileWindowWidth}
          />
          <WindowLabel>{window.title}</WindowLabel>        
        </WindowAndLabel>)
      : (
        <Window
          key={`${window.title}-${window.order}`}
          width={'30%'}
          type={window.type}
          onClick={() => (window.type === 'Video' ? showVideo(window.iframesrc) : '')}
          href={window.type === 'Video' ? window.iframesrc : window.url}
          imageAsset={window.image}
          onMouseEnter={() => windowOnMouseEnter(window.title, window.description)}
          onMouseLeave={windowOnMouseLeave}
          mobileWidth={mobileWindowWidth}
        />
      )
  }

  return (
    <Container className={className} numWindows={numWindows} windowLabelWidth={windowLabelWidth}>
      <RoomSide className={`room-side ${RoomSideEnum.Back}`}>
        <WallpaperBack src={config.EXPO_WALLPAPER.Inner} />

        {sortedWindows.map((window : UnionWindows, index : number) => {
          if (index < 3 || mobileView) {
            return backOrMobileWindow(window)
          }
        })}
      </RoomSide>

      <RoomSide className={RoomSideEnum.Right}>
        <Wallpaper src={config.EXPO_WALLPAPER.LeftRight} />
        <WallpaperShadowRight />

        {sortedWindows.map((window : UnionWindows, index : number) => {
          if (index >= 3 && index < 6 && !mobileView) {
            return (
              <Window
                key={`${window.title}-${window.order}`}
                width="30%"
                type={window.type}
                onClick={() => (window.type === 'Video' ? showVideo(window.iframesrc) : '')}
                href={window.type === 'Video' ? window.iframesrc : window.url}
                imageAsset={window.image}
                onMouseEnter={() => windowOnMouseEnter(window.title, window.description)}
                onMouseLeave={windowOnMouseLeave}
                hoverTransform="left-up"
              />
            )
          }
        })}

        <div
          style={{
            height: '100%',
            width: '50px',
          }}
        />
      </RoomSide>

      <RoomSide className={RoomSideEnum.Left} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Wallpaper src={config.EXPO_WALLPAPER.LeftRight} />

        {color && <WallpaperShadowLeft  style={{ background: `linear-gradient(-90deg, ${rgba(color, 0)} 0, ${rgba(color, 0.7)} 100%)` }} />}

        {!mobileView && room.logo && room.logoLink && (
          <CompanyLogoLink href={room.logoLink} target="_blank">
            <CompanyLogo src={`${config.LISA_IMAGE_BASE}${room.logo[0].assetId}&action=width:500`} alt={`company logo - ${room.company}`} />
          </CompanyLogoLink>
        )}
        {!mobileView && room.logo && !room.logoLink
        && (
          <CompanyLogoWithoutLink
            src={`${config.LISA_IMAGE_BASE}${room.logo[0].assetId}&action=width:500`}
            alt={`company logo - ${room.company}`}
          />
        )}
      </RoomSide>

      <RoomSide className={RoomSideEnum.Bottom}>
        <WallpaperFloor className="room-wallpaper" src={config.EXPO_WALLPAPER.Floor} />
      </RoomSide>

      <RoomSide className={RoomSideEnum.Top}>
        <WallpaperTop 
          className="room-wallpaper" 
          src={config.EXPO_WALLPAPER.Top} 
        />
        <WallpaperShadowTopBottom />
      </RoomSide>
    </Container>
  )
}

interface StyledStoryItemProps extends StoryItemProps {
  mobileWidth: number
}

const WindowAndLabel = styled.div<StyledStoryItemProps>`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  width: 20%;
  height: 70%;
  width: ${({ mobileWidth }) => `${mobileWidth}`};
  background-color: rgba(0, 0, 0, 0);
`

const WindowLabel = styled.p`
  padding-left: 20px;
  z-index: 1;
  font-family: ${({ theme }) => theme.fonts.fontFamilyPrimary};
  font-size: x-large;
  max-width: 70vw;
  color: ${({ theme }) => theme.colors.white};
`

interface ContainerProps {
  readonly numWindows: number
  readonly windowLabelWidth: number
}

const Container = styled.div<ContainerProps>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100vw;
  height: 100vh;
  margin: -50vh 0 0 -50vw;
  pointer-events: none;
  opacity: 0;
  transform-style: preserve-3d;

  @media ${mediaMax.mobileL} {
    width: ${props => `${props.numWindows * (100 + props.windowLabelWidth)}vw` };
  }

  &.active {
    opacity: 1;
  }
`

interface CustomGradientProps {
  color?: string
}

const CustomGradient = styled.div<CustomGradientProps>`
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  ${({ color }) => {
    const CustomColor = color || '#ffffff'
    return `
      background-image: linear-gradient(-90deg, ${rgba(CustomColor, 0)} 0, ${rgba(CustomColor, 1)} 100%);
    `
  }}
`

const CompanyLogo = styled.img`
  height: 100%;
  width: 100%;

  object-fit: contain;
  object-position: center;
`

const CompanyLogoLink = styled.a`
  display:block;
  position: absolute !important;
  left: 20%;
  width: 40%;
  max-height: 100%;
  height:80%;
  transform: translate3d(0, 0, 5px);
  cursor:pointer;
  pointer-events:all;

  img {
    object-fit: contain;
    width: 100%;
  }

  @media ${mediaMax.mobileL} {
    position: fixed !important;
    left: 2%;
    bottom: 2%;
    width: 25vw;
    height: 40px;
  }
`

const CompanyLogoWithoutLink = styled.img`
  width: 60%;
  height:60%;
  transform: translate3d(0, 0, 5px);
  object-fit: contain;
  object-position: center;
  img {
    object-fit: contain !important;
  }

  @media ${mediaMax.mobileL} {
    position: fixed !important;
    left: 2%;
    bottom: 2%;
    width: 25vw;
    height: 40px;
  }
`

const StyledStoryItem = styled(StoryItem)<StyledStoryItemProps>`
  background-color: rgba(0, 0, 0, 0);

  @media ${mediaMax.mobileL} {
    width: 90vw !important;
    height: 100%;

    img {
      height: 100%;
      width: 100%;
      object-fit: contain;
      object-position: center;
    }
  }
`
